/**
 * Format a number to es-AR currency format (e.g. 1.234,56)
 * Uses "." for thousands separator and "," for decimal separator
 */
export const formatMoney = (
  value: number | string | undefined | null,
): string => {
  if (value === undefined || value === null || value === '') return '';

  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '';

  return new Intl.NumberFormat('es-AR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: true,
  }).format(num);
};

/**
 * Format a number to es-AR decimal format (variable decimals)
 * Uses "." for thousands separator and "," for decimal separator
 */
export const formatDecimal = (
  value: number | string | undefined | null,
): string => {
  if (value === undefined || value === null || value === '') return '';

  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '';

  return new Intl.NumberFormat('es-AR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 4,
    useGrouping: true,
  }).format(num);
};

/**
 * Parse a localized string (e.g. "1.234,56") to a number (1234.56)
 */
export const parseMoney = (value: string): number | null => {
  if (!value) return null;

  // Remove thousands separators (.) and replace decimal separator (,) with (.)
  const cleanValue = value.replace(/\./g, '').replace(',', '.');

  const num = parseFloat(cleanValue);
  return isNaN(num) ? null : num;
};

/**
 * Format CUIT to XX-XXXXXXXX-X format
 */
export const formatCuit = (cuit: string): string => {
  if (!cuit) return '';

  // Eliminar cualquier guión existente
  const numbers = cuit.replace(/\D/g, '');

  // Formatear como XX-XXXXXXXX-X
  if (numbers.length === 11) {
    return `${numbers.slice(0, 2)}-${numbers.slice(2, 10)}-${numbers.slice(
      10,
    )}`;
  }

  return cuit; // Retornar sin cambios si no tiene 11 dígitos
};

export const normalizeCuitDigits = (cuit: string): string => {
  return (cuit || '').replace(/\D/g, '');
};

/**
 * Valida un CUIT sin exigir un tipo de titular concreto: acepta tanto persona
 * física (20, 23, 24, 27) como empresa (30, 33, 34). Debe quedar alineada con
 * `CuitValidator.isValid` del backend.
 */
export const isValidCuit = (cuit: string): boolean => {
  const digits = normalizeCuitDigits(cuit);
  if (!/^\d{11}$/.test(digits)) return false;

  const prefix = Number(digits.slice(0, 2));
  if (![20, 23, 24, 27, 30, 33, 34].includes(prefix)) {
    return false;
  }

  const weights = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2];
  const sum = weights.reduce((acc, weight, index) => {
    return acc + Number(digits[index]) * weight;
  }, 0);

  let verifier = 11 - (sum % 11);
  if (verifier === 11) verifier = 0;
  if (verifier === 10) verifier = 9;

  return verifier === Number(digits[10]);
};

/**
 * Format phone number based on country code
 */
export const formatPhone = (phone: string): string => {
  if (!phone) return '-';

  const numbers = phone.replace(/\D/g, '');

  // Argentina: celular canónico "549" + 10 dígitos
  if (numbers.startsWith('549') && numbers.length === 13) {
    return `+54 9 ${numbers.slice(3)}`;
  }
  // Argentina: "54" + número nacional (fijo, o celular sin el 9 en datos viejos)
  if (numbers.startsWith('54')) {
    return `+54 ${numbers.slice(2)}`;
  }

  // Si empieza con 1 (USA/Canada)
  if (numbers.startsWith('1') && numbers.length === 11) {
    return `+1 ${numbers.slice(1, 4)} ${numbers.slice(4, 7)} ${numbers.slice(
      7,
    )}`;
  }

  // Si empieza con 44 (UK)
  if (numbers.startsWith('44') && numbers.length >= 12) {
    return `+44 ${numbers.slice(2, 4)} ${numbers.slice(4, 8)} ${numbers.slice(
      8,
    )}`;
  }

  // Si no coincide con ningún formato, mostrar tira de números
  return numbers;
};
